/**
 * Pure display arithmetic for the post list (M1).
 *
 * Everything the table renders that is not a literal field is computed here so
 * it can be proved in node rather than driven through a virtualiser.
 */

import type { BlogClusterRow, BlogPostStatus, BlogReadiness } from "./types";

export type BadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "success"
  | "warning"
  | "info";

/**
 * How many missing locale codes are shown inline before the overflow count.
 *
 * Q7: the codes come first and the disclosure is progressive on top of them —
 * a reviewer must be able to read *which* languages are missing without
 * hovering anything.
 */
export const INLINE_MISSING_LOCALE_LIMIT = 4;

export interface MissingLocaleSummary {
  /** Up to `INLINE_MISSING_LOCALE_LIMIT` codes, in the order the server sent. */
  inline: string[];
  /** How many codes did not fit. `0` when they all did. */
  overflow: number;
  /** `'+9 more'`, or `''` when nothing overflowed. */
  overflowLabel: string;
  /** Every code, for the title/disclosure text. */
  all: string[];
}

export function summariseMissingLocales(missing: readonly string[]): MissingLocaleSummary {
  const inline = missing.slice(0, INLINE_MISSING_LOCALE_LIMIT);
  const overflow = missing.length - inline.length;
  return {
    inline,
    overflow,
    overflowLabel: overflow > 0 ? `+${String(overflow)} more` : "",
    all: [...missing],
  };
}

export interface ReadinessDisplay {
  label: string;
  variant: BadgeVariant;
  /** Whether this state stops a publish. */
  blocking: boolean;
}

/** Q6: one column that answers "can I ship this", not two facts to combine. */
export function describeReadiness(readiness: BlogReadiness): ReadinessDisplay {
  if (readiness.kind === "errors") {
    return {
      label: `Blocked by ${String(readiness.count)} ${readiness.count === 1 ? "error" : "errors"}`,
      variant: "destructive",
      blocking: true,
    };
  }
  if (readiness.kind === "missing-locales") {
    return {
      label: `Blocked by ${String(readiness.count)} missing ${
        readiness.count === 1 ? "locale" : "locales"
      }`,
      variant: "warning",
      blocking: true,
    };
  }
  return { label: "Ready to publish", variant: "success", blocking: false };
}

/**
 * Status chip styling.
 *
 * Q10's second half: `archived` must not share a variant with a clean drift
 * state, or a withdrawn post reads as a healthy one at a glance. `outline` is
 * already what "nothing to report" looks like elsewhere in the table, so
 * archived takes `secondary` and carries its own dimmed treatment.
 */
export function describeStatus(status: BlogPostStatus): { label: string; variant: BadgeVariant } {
  switch (status) {
    case "published":
      return { label: "published", variant: "success" };
    case "preview":
      return { label: "preview", variant: "info" };
    case "draft":
      return { label: "draft", variant: "outline" };
    default:
      return { label: "archived", variant: "secondary" };
  }
}

export interface LiveDisplay {
  /** The primary line: what readers are being served right now. */
  live: string;
  /** The drift stripe, or `''` when nothing is waiting to ship. */
  drift: string;
  /** The quiet second line. */
  detail: string;
  hasDrift: boolean;
}

/**
 * Q8: the Live cell splits into what is served and what is waiting.
 *
 * Q1 forbids a cluster-level version pair, so drift is expressed as a count of
 * locale copies that are ahead of what they published — never as `v12 → v14`.
 */
export function describeLive(row: {
  liveLocaleCount: number;
  unpublishedLocaleCount: number;
  localesWritten: number;
  missingLocales: readonly string[];
  status: BlogPostStatus;
}): LiveDisplay {
  const totalLocales = row.localesWritten + row.missingLocales.length;
  const hasDrift = row.unpublishedLocaleCount > 0;
  const drift = hasDrift
    ? `${String(row.unpublishedLocaleCount)} of ${String(totalLocales)} locales have unpublished changes`
    : "";

  if (row.status === "archived") {
    return {
      live: "Withdrawn",
      drift,
      detail: "no longer served to readers",
      hasDrift,
    };
  }
  if (row.liveLocaleCount === 0) {
    return {
      live: "Not published",
      drift,
      detail: "no revision has ever been served",
      hasDrift,
    };
  }
  return {
    live: `${String(row.liveLocaleCount)} of ${String(totalLocales)} locales live`,
    drift,
    detail: hasDrift ? "" : "up to date, nothing waiting to ship",
    hasDrift,
  };
}

/**
 * The header line above the table.
 *
 * Counts posts, not locales — the per-post detail is in the rows, and a summary
 * that mixed the two units would be unreadable.
 */
export function summariseClusterList(rows: readonly BlogClusterRow[]): string {
  const posts = rows.length;
  const live = rows.filter((row) => row.liveLocaleCount > 0).length;
  const drifting = rows.filter((row) => row.unpublishedLocaleCount > 0).length;
  const blocked = rows.filter((row) => row.readiness.kind === "errors").length;

  const parts = [`${String(posts)} ${posts === 1 ? "post" : "posts"}`, `${String(live)} live`];
  if (drifting > 0) {
    parts.push(`${String(drifting)} with unpublished changes`);
  }
  if (blocked > 0) {
    parts.push(`${String(blocked)} blocked by validation`);
  }
  return parts.join(" · ");
}

/** `'20/20'` — coverage as it reads in the cell. */
export function coverageLabel(row: {
  localesWritten: number;
  missingLocales: readonly string[];
}): string {
  const total = row.localesWritten + row.missingLocales.length;
  return `${String(row.localesWritten)}/${String(total)}`;
}
